import { useState } from "react";
import { PathPickerModal } from "./path-picker-modal.js";

// 3.A.4 (vaihe 3.A): "Lisää konventio" -modaali.
// Käyttäjä valitsee source-rootin dropdown:sta (vain ne joilla EI vielä
// ole konventiota näkyvät) + syöttää konvention templaatin.

export type AddConventionModalProps = {
  availableRoots: string[];
  /** Tallennus. */
  onAdd: (sourceRoot: string, convention: string, autoCreate: boolean) => Promise<void> | void;
  onClose: () => void;
  /** 3.A.7.4 — Daemonin machineId, mahdollistaa "Selaa Finderissa" -napin. */
  machineId?: string;
};

export function AddConventionModal({
  availableRoots,
  onAdd,
  onClose,
  machineId,
}: AddConventionModalProps) {
  const [selectedRoot, setSelectedRoot] = useState(availableRoots[0] ?? "");
  const [convention, setConvention] = useState("");
  const [autoCreate, setAutoCreate] = useState(false);
  const [busy, setBusy] = useState(false);
  const [showPicker, setShowPicker] = useState(false);

  const canSave =
    !busy && selectedRoot.length > 0 && convention.trim().length > 0;

  async function handleAdd() {
    if (!canSave) return;
    setBusy(true);
    try {
      await onAdd(selectedRoot, convention.trim(), autoCreate);
      onClose();
    } finally {
      setBusy(false);
    }
  }

  if (availableRoots.length === 0) {
    // Ei vapaita source-rooteja → näytä viesti modaalin sijaan
    return (
      <div
        className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="w-full max-w-md rounded-lg border border-slate-700 bg-slate-900 p-6 shadow-xl">
          <h3 className="text-lg font-semibold text-slate-100">
            Ei vapaita source-rootteja
          </h3>
          <p className="mt-3 text-sm text-slate-300">
            Kaikilla source-rooteilla on jo konventio. Jos haluat lisätä
            uudelle polulle, lisää ensin source-root yllä olevaan listaan.
          </p>
          <div className="mt-6 flex justify-end">
            <button
              type="button"
              onClick={onClose}
              className="rounded bg-slate-100 px-4 py-2 text-sm font-medium text-slate-900 hover:bg-white"
            >
              Sulje
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Esimerkki-placeholder valitulle rootille
  const placeholder = selectedRoot
    ? `${selectedRoot.replace(/\/+$/, "")}/{project}/`
    : "/Users/kimmos/Movies/{project}/";

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-xl rounded-lg border border-slate-700 bg-slate-900 p-6 shadow-xl">
        <h3 className="text-lg font-semibold text-slate-100">Lisää konventio</h3>

        <div className="mt-4 space-y-3">
          <div>
            <label className="block text-xs uppercase tracking-wide text-slate-400">
              Source-root
            </label>
            <select
              value={selectedRoot}
              onChange={(e) => setSelectedRoot(e.target.value)}
              disabled={busy}
              className="mt-1 w-full rounded bg-slate-800 px-3 py-2 text-sm text-slate-100"
            >
              {availableRoots.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-wide text-slate-400">
              Konventio
            </label>
            <div className="mt-1 flex gap-2">
              <input
                type="text"
                value={convention}
                onChange={(e) => setConvention(e.target.value)}
                placeholder={placeholder}
                disabled={busy}
                className="flex-1 rounded bg-slate-800 px-3 py-2 text-sm text-slate-100 placeholder-slate-500"
              />
              <button
                type="button"
                onClick={() => setShowPicker(true)}
                disabled={busy}
                className="rounded bg-slate-700 px-3 py-2 text-sm font-medium text-slate-100 hover:bg-slate-600 disabled:opacity-50"
                title="Selaa polku"
              >
                📁
              </button>
            </div>
            <p className="mt-1 text-xs text-slate-500">
              Tuetut tokenit:
              <code className="mx-1 rounded bg-slate-800 px-1">{`{project}`}</code>
              <code className="mx-1 rounded bg-slate-800 px-1">{`{date:YYYY-MM-DD}`}</code>
              <code className="mx-1 rounded bg-slate-800 px-1">{`{date:YYYY}`}</code>
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-300">
            <input
              type="checkbox"
              checked={autoCreate}
              onChange={(e) => setAutoCreate(e.target.checked)}
              disabled={busy}
            />
            Luo automaattisesti{" "}
            <code className="rounded bg-slate-800 px-1 text-xs">.kratti-project.yaml</code>{" "}
            kun konventio matchaa uutta kansiota
          </label>
        </div>

        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="rounded bg-slate-700 px-4 py-2 text-sm font-medium text-slate-100 hover:bg-slate-600 disabled:opacity-50"
          >
            Peruuta
          </button>
          <button
            type="button"
            onClick={() => void handleAdd()}
            disabled={!canSave}
            className="rounded bg-cyan-700 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-600 disabled:opacity-50"
          >
            {busy ? "Tallennetaan..." : "Lisää"}
          </button>
        </div>
      </div>
      {showPicker && (
        <PathPickerModal
          title="Selaa konvention pohjapolku"
          initialPath={convention}
          {...(machineId ? { machineId } : {})}
          onSelect={(p) => setConvention(p)}
          onClose={() => setShowPicker(false)}
        />
      )}
    </div>
  );
}
